import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  getLiveAccount,
  updateLiveAccount,
  formatSigned,
  toPercentSeries,
  CONNECTION_LABELS,
  CONSISTENCY_LABELS,
} from './liveTrackDataFeeds';
// Axis helpers are shared with the signal charts so every graph on the site is labelled the
// same way, rather than each page inventing its own scale.
import { niceScale, axisTimeTicks } from './signalDataFeeds';
import './SignalDetailPage.css';
import './LiveAccountPage.css';

// Live MT5 account detail.
//
// The hero is the THREE-WAY OVERLAY — the thing this whole two-phase build exists to make
// possible, and the one chart no competitor can draw:
//
//   backtest (dashed)   what the strategy did on history, before it went live
//   cloud (solid)       what OUR server executed forward, which cannot be faked
//   live MT5 (dotted)   what the user's own terminal reports
//
// All three are rebased to percent-from-start, because they begin from different deposits and
// plotting raw balances would let the largest account dominate and mean nothing.

function ThreeWayChart({ live, reference }) {
  const geometry = useMemo(() => {
    const series = [];
    if (reference?.backtest?.length) {
      series.push({ id: 'backtest', label: 'Backtest', points: toPercentSeries(reference.backtest, 'equity', 'barTime') });
    }
    if (reference?.cloud?.length) {
      series.push({ id: 'cloud', label: 'Cloud-verified', points: toPercentSeries(reference.cloud, 'equity', 'barTime') });
    }
    if (live?.length) {
      series.push({ id: 'live', label: 'Your MT5', points: toPercentSeries(live, 'equity', 'time') });
    }
    const drawable = series.filter((entry) => entry.points.length >= 2);
    if (!drawable.length) return null;

    const allTimes = drawable.flatMap((entry) => entry.points.map((point) => point.t));
    const allValues = drawable.flatMap((entry) => entry.points.map((point) => point.v));
    const minTime = Math.min(...allTimes);
    const maxTime = Math.max(...allTimes);
    // Zero is always included: on a percent-return chart the break-even line is the reference
    // every series is read against.
    const { ticks: valueTicks, niceMin, niceMax } = niceScale(
      Math.min(...allValues, 0),
      Math.max(...allValues, 0),
      5,
    );
    const timeRange = maxTime - minTime || 1;
    const valueRange = niceMax - niceMin || 1;
    const width = 1000;
    const height = 280;

    const x = (time) => ((time - minTime) / timeRange) * width;
    const y = (value) => height - ((value - niceMin) / valueRange) * (height - 24) - 12;

    return {
      width,
      height,
      zeroY: y(0),
      series: drawable.map((entry) => ({
        ...entry,
        path: entry.points
          .map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.t).toFixed(1)},${y(point.v).toFixed(1)}`)
          .join(' '),
        last: entry.points[entry.points.length - 1].v,
      })),
      // Ratios (0–1) so the labels can be positioned as HTML — text inside a
      // preserveAspectRatio="none" SVG is stretched horizontally and reads as broken.
      valueTicks: valueTicks
        .filter((value) => value >= niceMin && value <= niceMax)
        .map((value) => ({ value, ratio: y(value) / height })),
      timeTicks: axisTimeTicks(minTime, maxTime, 5),
      minValue: niceMin,
      maxValue: niceMax,
    };
  }, [live, reference]);

  if (!geometry) {
    return (
      <div className="signal-chart-empty">
        Not enough history yet — the comparison appears once your account has reported a few
        equity updates.
      </div>
    );
  }

  return (
    <div className="signal-chart-shell">
      {/* Canvas and the percent gutter are flex SIBLINGS, never one padded box: an absolutely
          positioned child resolves percentages against the padding box while the SVG spans only
          the content box, which silently pushes overlays off the curve. */}
      <div className="signal-chart-row">
        <div className="signal-chart-canvas">
          <svg viewBox={`0 0 ${geometry.width} ${geometry.height}`} className="live-chart" preserveAspectRatio="none">
            {geometry.valueTicks.map((tick) => (
              <line
                key={tick.value}
                x1="0" x2={geometry.width}
                y1={tick.ratio * geometry.height} y2={tick.ratio * geometry.height}
                className="signal-chart-grid"
              />
            ))}
            <line x1="0" x2={geometry.width} y1={geometry.zeroY} y2={geometry.zeroY} className="live-chart-zero" />
            {geometry.series.map((entry) => (
              <path key={entry.id} d={entry.path} className={`live-chart-${entry.id}`} />
            ))}
          </svg>
        </div>
        <div className="signal-chart-yaxis" aria-hidden="true">
          {geometry.valueTicks.map((tick) => (
            <span key={tick.value} style={{ top: `${(tick.ratio * 100).toFixed(2)}%` }}>
              {tick.value > 0 ? '+' : ''}{tick.value.toFixed(tick.value % 1 === 0 ? 0 : 1)}%
            </span>
          ))}
        </div>
      </div>
      <div className="signal-chart-axis-row">
        <div className="signal-chart-axis">
          {geometry.timeTicks.map((tick) => (
            <span key={tick.time} style={{ left: `${(tick.ratio * 100).toFixed(2)}%` }}>{tick.label}</span>
          ))}
        </div>
        <div className="signal-chart-axis-gutter" aria-hidden="true" />
      </div>
      <div className="live-chart-legend">
        {geometry.series.map((entry) => (
          <span key={entry.id}>
            <i className={`swatch ${entry.id}`} /> {entry.label}
            <strong className={entry.last >= 0 ? 'up' : 'down'}>
              {entry.last >= 0 ? '+' : ''}{entry.last.toFixed(1)}%
            </strong>
          </span>
        ))}
      </div>
      <p className="live-chart-note muted-text">
        All three are shown as percentage return from their own starting point, so accounts of
        different sizes can be compared directly.
      </p>
    </div>
  );
}

function LiveAccountPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useMemo(() => async () => {
    try {
      setData(await getLiveAccount(id));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // The terminal reports on its own schedule; refresh so an open page stays current.
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  if (error) {
    return (
      <div className="live-account-page page-shell">
        <p className="signal-error">{error}</p>
        <button type="button" className="ghost-button compact-button" onClick={() => navigate('/dashboard?tab=live')}>
          Back to live accounts
        </button>
      </div>
    );
  }
  if (!data) return <div className="live-account-page page-shell"><p className="muted-text">Loading…</p></div>;

  const { account, stats, trades, equity, consistency, reference, balanceOps } = data;
  const closedTrades = (trades || []).filter((trade) => trade.status === 'closed');

  return (
    <HelmetProvider>
      <div className="live-account-page page-shell">
        <Helmet><title>{account.broker || 'MT5'} #{account.accountLogin} — Live Account | Vix Algo Trading</title></Helmet>

        <header className="signal-detail-head">
          <div>
            <button type="button" className="signal-back" onClick={() => navigate('/dashboard?tab=live')}>
              ← Live accounts
            </button>
            <h1>{account.broker || 'MetaTrader account'}</h1>
            <p className="muted-text">
              #{account.accountLogin} · {account.accountType} · {account.currency}
              {account.leverage ? ` · 1:${account.leverage}` : ''}
              {account.eaName ? ` · ${account.eaName}` : ''}
            </p>
          </div>
          <div className="signal-detail-actions">
            <span className={`signal-pill ${account.connection === 'live' ? 'ok' : account.connection === 'idle' ? 'warn' : ''}`}>
              {CONNECTION_LABELS[account.connection] || account.connection}
            </span>
          </div>
        </header>

        {/* The honesty banner. This data comes from the user's own machine, and saying so
            plainly is what keeps the "cloud-verified" label on signals meaningful. */}
        <div className="live-selfreport">
          <strong>Self-reported</strong>
          <span className="muted-text">
            These figures are sent by the EA running in your MetaTrader terminal. We did not
            execute these trades.
            {consistency?.assessed && ' The comparison below is our independent check.'}
          </span>
        </div>

        {consistency?.assessed && (
          <div className={`live-consistency live-consistency-${consistency.verdict}`}>
            <div>
              <strong>{CONSISTENCY_LABELS[consistency.verdict]} — {Math.round(consistency.score)}%</strong>
              <p className="muted-text">
                {consistency.matched} of {consistency.signalTrades} trades from our own cloud run
                of this strategy also appear in your account
                {consistency.liveOnly > 0 && `, and ${consistency.liveOnly} of yours are not in ours`}.
                {' '}Differences are normal — a different broker, slippage, or the EA being off
                for a while will all show up here.
              </p>
            </div>
          </div>
        )}

        <ThreeWayChart live={equity} reference={reference} />

        <section className="signal-stat-row">
          <div className="signal-stat">
            <span className="muted-text">Balance</span>
            <strong>{Number(account.balance || 0).toFixed(2)}</strong>
            <small className="muted-text">{account.currency}</small>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Equity</span>
            <strong>{Number(account.equity || 0).toFixed(2)}</strong>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Return</span>
            <strong className={stats.returnPct >= 0 ? 'up' : 'down'}>
              {stats.returnPct >= 0 ? '+' : ''}{stats.returnPct.toFixed(2)}%
            </strong>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Trades</span>
            <strong>{stats.trades}</strong>
            <small className="muted-text">{stats.openTrades} open</small>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Win rate</span>
            <strong>{stats.trades ? `${stats.winRate.toFixed(0)}%` : '—'}</strong>
            <small className="muted-text">{stats.wins}W / {stats.losses}L</small>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Profit factor</span>
            <strong>{stats.profitFactor === null ? '—' : stats.profitFactor.toFixed(2)}</strong>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Max drawdown</span>
            <strong>{stats.maxDrawdownPct.toFixed(1)}%</strong>
          </div>
          <div className="signal-stat">
            <span className="muted-text">Costs</span>
            <strong>{formatSigned(stats.totalCommission + stats.totalSwap)}</strong>
            <small className="muted-text">commission + swap</small>
          </div>
        </section>

        <div className="signal-detail-grid">
          <section className="signal-panel">
            <h3>Trades</h3>
            <div className="table-shell">
              <table>
                <thead>
                  <tr><th>Side</th><th>Symbol</th><th>Opened</th><th>Closed</th><th>Volume</th><th>Result</th></tr>
                </thead>
                <tbody>
                  {closedTrades.length === 0 ? (
                    <tr><td colSpan="6" className="empty-cell">No closed trades reported yet.</td></tr>
                  ) : [...closedTrades].reverse().slice(0, 60).map((trade) => (
                    <tr key={trade.positionId}>
                      <td><span className={`signal-side ${trade.side === 'BUY' ? 'up' : 'down'}`}>{trade.side}</span></td>
                      <td>{trade.symbol}</td>
                      <td className="muted-text">{new Date(trade.openTime).toLocaleString()}</td>
                      <td className="muted-text">{trade.closeTime ? new Date(trade.closeTime).toLocaleString() : '—'}</td>
                      <td>{Number(trade.volume).toFixed(2)}</td>
                      <td className={trade.netProfit >= 0 ? 'up' : 'down'}>{formatSigned(trade.netProfit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="signal-side-column">
            {(balanceOps || []).length > 0 && (
              <section className="signal-panel">
                <h3>Deposits &amp; withdrawals</h3>
                {/* Shown separately because they move the balance without being trading
                    results — folding them in would overstate performance. */}
                <ul className="signal-event-list">
                  {balanceOps.slice(-8).reverse().map((op) => (
                    <li key={op.dealTicket}>
                      <strong className={op.amount >= 0 ? 'up' : 'down'}>{formatSigned(op.amount)}</strong>
                      <span className="muted-text">
                        {new Date(op.time).toLocaleDateString()}{op.comment ? ` · ${op.comment}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="muted-text">Excluded from the trading statistics above.</p>
              </section>
            )}

            {data.strategyId && (
              <section className="signal-panel">
                <h3>Strategy</h3>
                <p className="muted-text">
                  This account is linked to a saved strategy, which is what makes the comparison
                  above possible.
                </p>
                <div className="inline-actions">
                  <button
                    type="button"
                    className="ghost-button compact-button"
                    onClick={() => navigate(`/tester?strategy=${data.strategyId}`)}
                  >
                    Open in Builder
                  </button>
                  {reference?.deploymentId && (
                    <button
                      type="button"
                      className="ghost-button compact-button"
                      onClick={() => navigate(`/signals/${reference.deploymentId}`)}
                    >
                      Cloud signal
                    </button>
                  )}
                </div>
              </section>
            )}

            {data.isOwner && (
              <section className="signal-panel">
                <h3>Visibility</h3>
                <select
                  value={account.visibility}
                  onChange={async (event) => {
                    await updateLiveAccount(id, { visibility: event.target.value });
                    load();
                  }}
                >
                  <option value="private">Private — only you</option>
                  <option value="unlisted">Unlisted — anyone with the link</option>
                  <option value="public">Public</option>
                </select>
                <p className="muted-text">
                  Sharing shows your results and broker, never your login or credentials.
                </p>
              </section>
            )}
          </aside>
        </div>
      </div>
    </HelmetProvider>
  );
}

export default LiveAccountPage;
